// Silent-first, redirect-fallback access-token getter for the CRM API.
// Clone of the cmms-workorder portal pattern (adapted to this portal's
// existing tokenRequest scopes). Components get a stable async getter and
// pass the token into src/api/client.ts calls — the client itself never
// touches MSAL (keeps it usable outside React and testable).

import { useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { loginRequest, tokenRequest } from './msalConfig';
import { arrivalLoginHint, signedInAccount } from './ssoMemory';
import { linkLoginHint } from './linkHint';

export function useApiToken(): () => Promise<string> {
  const { instance, accounts } = useMsal();

  return useCallback(async () => {
    // The ACTIVE account — the link's person, or whoever just signed in.
    const account = signedInAccount(instance, accounts);
    if (!account) {
      // Name who, so Entra signs them in from their session instead of
      // showing a picker (every link lands signed in, auth/ssoMemory.ts).
      const hint = arrivalLoginHint(linkLoginHint(window.location.search, window.location.hash));
      await instance.loginRedirect({ ...loginRequest, ...(hint ? { loginHint: hint } : {}) });
      throw new Error('Sign-in redirect initiated');
    }
    try {
      const result = await instance.acquireTokenSilent({ account, ...tokenRequest });
      return result.accessToken;
    } catch (err) {
      if (err instanceof InteractionRequiredAuthError) {
        await instance.acquireTokenRedirect({ account, ...tokenRequest });
        throw new Error('Interactive token acquisition redirect initiated');
      }
      throw err;
    }
  }, [instance, accounts]);
}
